import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Shield, CheckSquare, Square, Loader2, AlertCircle, Users, Edit, Trash2 } from 'lucide-react';
import { apiFetch } from '../../utils/api';

const UserManagement: React.FC = () => {
    const [subAdmins, setSubAdmins] = useState<any[]>([]);
    const [pages, setPages] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [newAdmin, setNewAdmin] = useState({
        username: '',
        password: '',
        page_ids: [] as number[]
    });

    const [editAdmin, setEditAdmin] = useState<any>(null);
    const [showEditModal, setShowEditModal] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [adminsRes, pagesRes] = await Promise.all([
                apiFetch('/admin/sub-admins'),
                apiFetch('/admin/pages')
            ]);

            if (adminsRes.ok && pagesRes.ok) {
                setSubAdmins(await adminsRes.json());
                setPages(await pagesRes.json());
            } else {
                const data = await adminsRes.json();
                setError(data.message || 'Failed to load data from server.');
            }
        } catch (err) {
            setError('Error connecting to API.');
        } finally {
            // Loading state removed
        }
    }, []);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData]);

    const handleCreateAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        try {
            const response = await apiFetch('/admin/sub-admins', {
                method: 'POST',
                body: JSON.stringify(newAdmin)
            });

            if (response.ok) {
                setNewAdmin({ username: '', password: '', page_ids: [] });
                fetchData();
                alert('Sub-admin created successfully');
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to create sub-admin.');
            }
        } catch (err) {
            setError('Error connecting to API.');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await apiFetch(`/admin/sub-admins/${editAdmin.id}`, {
                method: 'PUT',
                body: JSON.stringify(editAdmin)
            });
            if (res.ok) {
                alert('Sub-admin updated successfully');
                setShowEditModal(false);
                fetchData();
            } else {
                const data = await res.json();
                alert(data.message || 'Failed to update sub-admin');
            }
        } catch (err) {
            console.error(err);
            alert('Error updating sub-admin');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAdmin = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this sub-admin?')) {
            try {
                const res = await apiFetch(`/admin/sub-admins/${id}`, {
                    method: 'DELETE'
                });
                if (res.ok) {
                    alert('Sub-admin deleted successfully');
                    fetchData();
                } else {
                    const data = await res.json();
                    alert(data.message || 'Failed to delete sub-admin');
                }
            } catch (err) {
                console.error(err);
                alert('Error deleting sub-admin');
            }
        }
    };

    const togglePermission = (pageId: number, isEdit: boolean = false) => {
        if (isEdit) {
            setEditAdmin((prev: any) => ({
                ...prev,
                page_ids: prev.page_ids?.includes(pageId)
                    ? prev.page_ids.filter((id: number) => id !== pageId)
                    : [...(prev.page_ids || []), pageId]
            }));
        } else {
            setNewAdmin(prev => ({
                ...prev,
                page_ids: prev.page_ids.includes(pageId)
                    ? prev.page_ids.filter(id => id !== pageId)
                    : [...prev.page_ids, pageId]
            }));
        }
    };

    const openEditModal = (admin: any) => {
        // Transform permissions array to page_ids array if needed
        // Admin object from API has permissions: [{page_id: 1, ...}, ...]
        // But update API expects page_ids: [1, 2]
        // Actually, fetching sub-admins returns logic from dv_get_sub_admins which returns JSON array permissions
        const pageIds = admin.permissions ? admin.permissions.map((p: any) => p.page_id) : [];
        setEditAdmin({ ...admin, password: '', page_ids: pageIds });
        setShowEditModal(true);
    };

    return (
        <div className="space-y-10">
            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 flex items-center gap-3 animate-pulse">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gray-50/50 p-6 border-b border-gray-100 flex items-center gap-3">
                    <UserPlus className="text-blue-600" size={24} />
                    <h2 className="text-xl font-bold text-gray-800 tracking-tight">Create Sub-Admin</h2>
                </div>

                <form onSubmit={handleCreateAdmin} className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider">Username</label>
                            <input
                                type="text"
                                value={newAdmin.username}
                                onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none bg-gray-50 focus:bg-white"
                                placeholder="Admin username"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider">Password</label>
                            <input
                                type="password"
                                value={newAdmin.password}
                                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none bg-gray-50 focus:bg-white"
                                placeholder="Set secure password"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={saving}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2 transform active:scale-95"
                        >
                            {saving ? <Loader2 className="animate-spin" /> : <Shield size={20} />}
                            Register Sub-Admin
                        </button>
                    </div>

                    <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100">
                        <label className="block text-sm font-bold text-blue-900 mb-4 uppercase tracking-widest">Page Access Control</label>
                        <div className="space-y-3">
                            {pages.map(page => (
                                <div
                                    key={page.id}
                                    onClick={() => togglePermission(page.id)}
                                    className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all border ${newAdmin.page_ids.includes(page.id)
                                        ? 'bg-blue-600 text-white border-transparent shadow-md'
                                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    {newAdmin.page_ids.includes(page.id) ? <CheckSquare size={20} /> : <Square size={20} />}
                                    <div className="flex flex-col">
                                        <span className="font-bold text-sm tracking-tight">{page.page_name}</span>
                                        <span className={`text-[10px] uppercase font-bold tracking-widest ${newAdmin.page_ids.includes(page.id) ? 'text-blue-100' : 'text-gray-400'}`}>
                                            Path: {page.route}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </form>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gray-50/50 p-6 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Users className="text-blue-600" size={24} />
                        <h2 className="text-xl font-bold text-gray-800 tracking-tight">Existing Sub-Admins</h2>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50/50">
                            <tr>
                                <th className="px-8 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest">Username</th>
                                <th className="px-8 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest">Created At</th>
                                <th className="px-8 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                <th className="px-8 py-5 text-xs font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {subAdmins.map(admin => (
                                <tr key={admin.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">
                                                {admin.username.charAt(0).toUpperCase()}
                                            </div>
                                            <span className="font-bold text-gray-900">{admin.username}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 text-sm font-medium text-gray-500">
                                        {new Date(admin.created_at).toLocaleDateString(undefined, {
                                            year: 'numeric', month: 'short', day: 'numeric'
                                        })}
                                    </td>
                                    <td className="px-8 py-5 text-sm">
                                        <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                            Active
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 flex gap-4">
                                        <button onClick={() => openEditModal(admin)} className="text-blue-600 hover:text-blue-800" title="Edit">
                                            <Edit size={18} />
                                        </button>
                                        <button onClick={() => handleDeleteAdmin(admin.id)} className="text-red-500 hover:text-red-700" title="Delete">
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && editAdmin && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-800">Edit Sub-Admin</h3>
                            <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">Close</button>
                        </div>
                        <form onSubmit={handleUpdateAdmin} className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider">Username</label>
                                    <input
                                        type="text"
                                        value={editAdmin.username}
                                        onChange={(e) => setEditAdmin({ ...editAdmin, username: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 bg-gray-50"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider">New Password (Optional)</label>
                                    <input
                                        type="password"
                                        value={editAdmin.password || ''}
                                        onChange={(e) => setEditAdmin({ ...editAdmin, password: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                                        placeholder="Leave blank to keep current"
                                    />
                                </div>
                            </div>

                            <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100">
                                <label className="block text-sm font-bold text-blue-900 mb-4 uppercase tracking-widest">Page Access Control</label>
                                <div className="space-y-3">
                                    {pages.map(page => (
                                        <div
                                            key={page.id}
                                            onClick={() => togglePermission(page.id, true)}
                                            className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all border ${editAdmin.page_ids?.includes(page.id)
                                                ? 'bg-blue-600 text-white border-transparent shadow-md'
                                                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            {editAdmin.page_ids?.includes(page.id) ? <CheckSquare size={20} /> : <Square size={20} />}
                                            <div className="flex flex-col">
                                                <span className="font-bold text-sm tracking-tight">{page.page_name}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="col-span-1 lg:col-span-2 flex justify-end gap-4 pt-4">
                                <button type="button" onClick={() => setShowEditModal(false)} className="px-6 py-3 text-gray-600 font-bold hover:bg-gray-100 rounded-xl">Cancel</button>
                                <button type="submit" disabled={saving} className="px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200">
                                    {saving ? <Loader2 className="animate-spin" /> : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
